/**
 * LegalPrivacyPage — エールチャット プライバシーポリシー
 *
 * 個人情報保護法・GDPR / CCPA の一般的構成に準拠した B2B SaaS 想定の本文。
 * ライトテーマ専用。design tokens (CSS variables) を直接利用し、Tailwind 4
 * + 既存トークンの範囲内で `prose` 相当の読みやすい長文レイアウトを構築する。
 *
 * ルート: `/legal/privacy`
 */
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export function LegalPrivacyPage(): JSX.Element {
  return (
    <div className="min-h-screen bg-surface-base">
      <main
        id="legal-main"
        className="mx-auto w-full max-w-3xl px-4 py-10 sm:px-6 sm:py-16"
        aria-labelledby="legal-privacy-heading"
      >
        <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-brand-700">
          Legal / プライバシー
        </p>
        <h1
          id="legal-privacy-heading"
          className="mb-2 text-3xl font-bold leading-tight text-fg-primary"
        >
          プライバシーポリシー
        </h1>
        <p className="mb-8 text-sm text-fg-muted">
          最終改定日: 2026年9月15日
        </p>

        <article className="space-y-8 text-base leading-relaxed text-fg-primary">
          <section>
            <h2 className="mb-3 text-xl font-bold text-fg-primary">
              第1条 (目的)
            </h2>
            <p className="text-fg-secondary">
              本プライバシーポリシー（以下「本ポリシー」といいます。）は、エールチャット（以下「当社」といいます。）が提供する個人・法人向けコミュニケーションサービス「エールチャット」（以下「本サービス」といいます。）における、ユーザーの個人情報の取扱いについて定めるものです。
              当社は、個人情報の保護に関する法律（以下「個人情報保護法」といいます。）、関連法令、ガイドライン、および本ポリシーを遵守し、ユーザーの個人情報を適正に取り扱います。
              また、欧州経済領域 (EEA) におけるユーザーに対しては EU 一般データ保護規則 (GDPR)、米国カリフォルニア州在住のユーザーに対してはカリフォルニア州消費者プライバシー法 (CCPA / CPRA) を踏まえた取扱いを行います。
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-fg-primary">
              第2条 (取得する情報)
            </h2>
            <p className="mb-3 text-fg-secondary">
              当社は、本サービスの提供にあたり、以下の情報を取得します。
            </p>
            <ul className="list-disc space-y-1.5 pl-6 text-fg-secondary">
              <li>
                <strong className="text-fg-primary">アカウント情報:</strong>{" "}
                氏名、メールアドレス、所属組織名、役職、表示名、プロフィール画像、認証連携先 (Google / Microsoft 等) の識別子
              </li>
              <li>
                <strong className="text-fg-primary">認証情報:</strong>{" "}
                マジックリンク、ワンタイムコード、OAuth トークン、SAML アサーション、リフレッシュトークン、最終サインイン日時、サインイン手段
              </li>
              <li>
                <strong className="text-fg-primary">コミュニケーション情報:</strong>{" "}
                ルーム / チャンネル名、メッセージ本文、添付ファイル、メンション、リアクション、既読状態、タスク、通話履歴
              </li>
              <li>
                <strong className="text-fg-primary">利用ログ:</strong>{" "}
                アクセス日時、IP アドレス、ブラウザ User-Agent、参照元 URL、画面遷移、操作イベント
              </li>
              <li>
                <strong className="text-fg-primary">端末・環境情報:</strong>{" "}
                OS 種別、ブラウザ種別、画面解像度、言語設定、タイムゾーン
              </li>
              <li>
                <strong className="text-fg-primary">課金情報:</strong>{" "}
                請求先組織名、請求先メールアドレス、支払方法種別 (クレジットカード / 銀行振込 等)。クレジットカード番号そのものは決済代行事業者で処理し、当社は保持しません。
              </li>
              <li>
                <strong className="text-fg-primary">問い合わせ情報:</strong>{" "}
                サポート問い合わせ内容、添付スクリーンショット、対応履歴
              </li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-fg-primary">
              第3条 (利用目的)
            </h2>
            <p className="mb-3 text-fg-secondary">
              当社は、取得した情報を以下の目的の範囲内で利用します。
            </p>
            <ul className="list-disc space-y-1.5 pl-6 text-fg-secondary">
              <li>本サービスの提供、運用、保守、改善および新機能の開発</li>
              <li>ユーザー認証、アカウント管理、本人確認、不正利用の防止</li>
              <li>料金の請求、支払処理、収納代行業者への必要情報の連携</li>
              <li>ユーザーへの重要なお知らせ、利用規約や本ポリシーの変更通知</li>
              <li>サポート対応、問い合わせへの回答、障害時の連絡</li>
              <li>利用状況の統計解析、サービス品質の改善 (個人を識別できない統計値に限る)</li>
              <li>個人ワークスペースにおける、当社と直接契約したスポンサーの広告表示</li>
              <li>法令、ガイドライン、裁判所の命令等への対応</li>
            </ul>
          </section>

          <section aria-labelledby="sponsor-privacy-heading">
            <h2 id="sponsor-privacy-heading" className="mb-3 text-xl font-bold text-fg-primary">スポンサー広告について</h2>
            <p className="text-fg-secondary">広告は、個人ワークスペースで実際のスポンサー契約がある場合にだけ表示します。法人ワークスペースと利用区分が未設定のワークスペースには表示しません。会話本文、ルーム名、検索内容、添付ファイル、通知、連絡先を広告の選定や広告事業者への提供に利用しません。広告のための第三者スクリプト、追跡 Cookie、広告識別子は使用せず、表示やクリックの追跡リクエストも送りません。</p>
            <p className="mt-3 text-fg-secondary">広告には「広告」とスポンサー名を表示します。広告を選択するとスポンサーの外部サイトへ移動し、移動先のプライバシーポリシーが適用されます。当社の認証情報や会話情報を移動先へ付与しません。スポンサー契約がない場合は広告を表示しません。</p>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-fg-primary">
              第4条 (第三者提供)
            </h2>
            <p className="mb-3 text-fg-secondary">
              当社は、以下のいずれかに該当する場合を除き、あらかじめユーザーの同意を得ることなく個人情報を第三者に提供しません。
            </p>
            <ul className="list-disc space-y-1.5 pl-6 text-fg-secondary">
              <li>法令に基づく場合</li>
              <li>人の生命、身体または財産の保護のために必要がある場合であって、ユーザーの同意を得ることが困難な場合</li>
              <li>公衆衛生の向上または児童の健全な育成の推進のために特に必要がある場合であって、ユーザーの同意を得ることが困難な場合</li>
              <li>国の機関若しくは地方公共団体又はその委託を受けた者が法令の定める事務を遂行することに対して協力する必要がある場合</li>
              <li>合併、会社分割、事業譲渡その他の事由により事業が承継される場合</li>
              <li>ユーザーが所属する法人組織の管理者に対して、業務管理上必要な情報を提供する場合</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-fg-primary">
              第5条 (委託先)
            </h2>
            <p className="mb-3 text-fg-secondary">
              当社は、本サービスの提供にあたり、業務の一部を以下の類型の委託先に委託することがあります。委託先には、必要かつ適切な監督を行います。
            </p>
            <ul className="list-disc space-y-1.5 pl-6 text-fg-secondary">
              <li>クラウドインフラストラクチャ事業者 (Cloudflare Inc. 等)</li>
              <li>データベース、ストレージ、CDN 提供事業者</li>
              <li>メール配信、SMS 配信、認証連携 (OAuth / SAML) 事業者</li>
              <li>決済代行事業者</li>
              <li>サポートツール、顧客管理 (CRM) ツール提供事業者</li>
              <li>セキュリティ監視、不正検知サービス提供事業者</li>
            </ul>
            <p className="mt-3 text-fg-secondary">
              一部の委託先は日本国外 (アメリカ合衆国、欧州連合加盟国等) に所在することがあります。当社は越境移転に関し、個人情報保護法第28条および GDPR 第46条に定める適切な保護措置（標準契約条項等）を講じます。
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-fg-primary">
              第6条 (保存期間)
            </h2>
            <p className="text-fg-secondary">
              当社は、利用目的の達成に必要な期間に限り、個人情報を保存します。具体的な保存期間は情報の種類により異なります。アカウント情報はサービス利用期間中、個人プランの履歴・添付・検索索引は 90 日間保持されます。法人 Standard プランにはプランによる履歴期限を設けません。これらの保持期間に加え、ワークスペースまたはルームで明示的に設定した保持期限、利用者の削除操作、アカウント・組織の削除方針に従います。共有メッセージや保存ファイルは、アカウント削除後も参加者の権限を保つ組織記録として残る場合があります。利用ログは原則として13ヶ月、課金情報は法定保存期間 (税法上は7年) のあいだ保存します。
              ユーザーからアカウント削除の申請を受けた場合、当社は遅滞なく該当情報を削除または匿名化しますが、法令上の保存義務がある情報、紛争解決のために必要な情報は当該期間が経過するまで保存することがあります。
              アカウント削除の手順、削除対象および保持対象の詳細は、
              <Link to="/legal/account-deletion" className="text-brand-700 hover:underline">
                アカウント削除のご案内
              </Link>
              をご覧ください。
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-fg-primary">
              第7条 (安全管理措置)
            </h2>
            <p className="mb-3 text-fg-secondary">
              当社は、取扱う個人情報について漏えい、滅失、毀損の防止その他の安全管理のために必要かつ適切な措置を講じます。
            </p>
            <ul className="list-disc space-y-1.5 pl-6 text-fg-secondary">
              <li>
                <strong className="text-fg-primary">組織的安全管理:</strong>{" "}
                情報セキュリティ責任者の設置、内部規程の整備、アクセス記録の保管および定期監査
              </li>
              <li>
                <strong className="text-fg-primary">人的安全管理:</strong>{" "}
                従業員への秘密保持誓約取得、定期教育、退職時のアカウント無効化
              </li>
              <li>
                <strong className="text-fg-primary">物理的安全管理:</strong>{" "}
                データセンターへの入退室管理、機器の盗難・紛失対策
              </li>
              <li>
                <strong className="text-fg-primary">技術的安全管理:</strong>{" "}
                通信経路の TLS 1.3 暗号化、保存時の AES-256 暗号化、最小権限原則に基づく権限管理、多要素認証、脆弱性診断および侵入テストの定期実施
              </li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-fg-primary">
              第8条 (開示請求等の窓口)
            </h2>
            <p className="mb-3 text-fg-secondary">
              ユーザーは、当社に対し、自己に関する保有個人データの開示、訂正、追加、削除、利用停止、第三者提供の停止 (以下総称して「開示等」といいます。) を請求することができます。GDPR が適用される場合、アクセス権、訂正権、消去権 (忘れられる権利)、処理制限権、データポータビリティ権、異議申立権を行使することもできます。
            </p>
            <p className="text-fg-secondary">
              開示等の請求は、下記「第10条 (お問い合わせ先)」記載の連絡先までご連絡ください。当社は本人確認を行ったうえで、原則として30日以内に対応します。
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-fg-primary">
              第9条 (Cookie および類似技術)
            </h2>
            <p className="text-fg-secondary">
              当社は、本サービスの利便性向上、認証セッション維持、不正アクセス検知、利用状況解析のために Cookie および類似技術 (LocalStorage、SessionStorage 等) を使用します。ユーザーはブラウザの設定により Cookie を無効化することが可能ですが、無効化した場合、本サービスの一部機能をご利用いただけないことがあります。
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-fg-primary">
              第10条 (お問い合わせ先)
            </h2>
            <p className="text-fg-secondary">
              本ポリシーに関するお問い合わせ、開示等の請求、その他個人情報の取扱いに関するご質問は、下記までお願いいたします。
            </p>
            <address className="mt-3 not-italic rounded-md border border-border-subtle bg-canvas p-4 text-sm text-fg-secondary">
              <div className="font-bold text-fg-primary">エールチャット 個人情報保護管理者</div>
              <div className="mt-1">メールアドレス: support@skillbridge.co.jp</div>
              <div>受付時間: 平日 10:00 – 18:00 (日本時間 / 土日祝・年末年始を除く)</div>
            </address>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-fg-primary">
              第11条 (改定履歴)
            </h2>
            <ul className="list-disc space-y-1.5 pl-6 text-fg-secondary">
              <li>2026年9月15日 - アカウント削除の公開案内へのリンクを追加</li>
              <li>2026年9月8日 - 個人プランの90日保持と法人プランの保持条件を明記</li>
              <li>2026年9月6日 - 個人・法人の利用区分、料金、スポンサー広告の方針を更新</li>
              <li>2026年5月20日 — 初版公開</li>
            </ul>
            <p className="mt-3 text-fg-secondary">
              当社は、必要に応じて本ポリシーを変更することがあります。重要な変更を行う場合は、本サービス内の通知またはメールにより、ユーザーに事前にお知らせします。
            </p>
          </section>
        </article>

        <footer className="mt-12 border-t border-border-subtle pt-6">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 rounded px-1 py-0.5 text-sm text-brand-700 hover:underline focus-visible:shadow-focus"
          >
            <ArrowLeft size={14} aria-hidden /> ログイン画面に戻る
          </Link>
        </footer>
      </main>
    </div>
  );
}
